import styled from '@emotion/styled'
import {
  ExtranetCustomerUserProfessionalRoleDto,
  ExtranetCustomerUserRoleDto,
  ExtranetUserDto,
} from '@pharmac-extranet/extranet-api'
import { ExtranetEditButton, useLanguage } from '@pharmac-extranet/shared'
import {
  PharmacAccordion,
  PharmacAccordionContent,
  PharmacAccordionHandle,
  PharmacColumn,
  PharmacContainer,
  PharmacH6,
  PharmacRow,
  PharmacText,
} from '@pharmac/ui'
import { useNavigate } from 'react-router-dom'
import { useExtranetPath } from '../../router/useExtranetPath'

const ButtonRow = styled(PharmacRow)`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`

type Props = {
  customerUsers: ExtranetUserDto[]
}

export const CustomerUserListMobileTable = ({ customerUsers }: Props) => {
  const { t, key } = useLanguage(['extranet', 'common'])
  const navigate = useNavigate()
  const createLink = useExtranetPath()

  const sortOrder = (a: ExtranetUserDto, b: ExtranetUserDto) => {
    const collator = new Intl.Collator(['fi', 'sv'], { numeric: true, sensitivity: 'base' })
    const nameDiff = collator.compare(a.lastName, b.lastName)

    if (nameDiff !== 0) return nameDiff

    if (a.lastName > b.lastName) return -1
    if (b.lastName > a.lastName) return 1

    return 0
  }

  const sortedByLastname = customerUsers.sort(sortOrder)

  return (
    <>
      {sortedByLastname?.map((a: ExtranetUserDto) => (
        <PharmacAccordion
          key={a.id}
          marginSmallBottom
          handle={({ isOpen }: any) => (
            <PharmacAccordionHandle
              variant="secondary"
              isOpen={isOpen}
            >
              <PharmacText
                style={{ fontSize: '18px' }}
                marginSmallRight
              >
                {a.firstName} {a.lastName}
              </PharmacText>
            </PharmacAccordionHandle>
          )}
        >
          <PharmacAccordionContent variant="secondary">
            <ButtonRow>
              <ExtranetEditButton
                onClick={() => navigate(createLink('oma-annos.customership.user', { id: a.id }))}
              />
            </ButtonRow>
            <PharmacContainer>
              <PharmacColumn paddingLargeLeft>
                <PharmacH6>{t('userName', { ns: 'common' })}</PharmacH6>
                <PharmacContainer
                  marginMediumTop
                  marginLargeBottom
                >
                  <PharmacText>{a.userName || '-'}</PharmacText>
                </PharmacContainer>
                <PharmacH6>{t('emailAddress', { ns: 'common' })}</PharmacH6>
                <PharmacContainer
                  marginMediumTop
                  marginLargeBottom
                >
                  <PharmacText>{a.emailAddress || '-'}</PharmacText>
                </PharmacContainer>
                <PharmacH6>{t('phoneNumber', { ns: 'common' })}</PharmacH6>
                <PharmacContainer
                  marginMediumTop
                  marginLargeBottom
                >
                  <PharmacText>{a.phoneNumber || '-'}</PharmacText>
                </PharmacContainer>
                <PharmacH6>{t('streetName', { ns: 'common' })}</PharmacH6>
                <PharmacContainer
                  marginMediumTop
                  marginLargeBottom
                >
                  <PharmacText>{a.streetName || '-'}</PharmacText>
                </PharmacContainer>
                <PharmacH6>{t('postCode', { ns: 'common' })}</PharmacH6>
                <PharmacContainer
                  marginMediumTop
                  marginLargeBottom
                >
                  <PharmacText>{a.postCode || '-'}</PharmacText>
                </PharmacContainer>
                <PharmacH6>{t('townName', { ns: 'common' })}</PharmacH6>
                <PharmacContainer
                  marginMediumTop
                  marginLargeBottom
                >
                  <PharmacText>{a.townName || '-'}</PharmacText>
                </PharmacContainer>
                <PharmacH6>{t('country', { ns: 'common' })}</PharmacH6>
                <PharmacContainer
                  marginMediumTop
                  marginLargeBottom
                >
                  <PharmacText>{a.countryName || '-'}</PharmacText>
                </PharmacContainer>
                <PharmacH6>{t('certificateCardNumber', { ns: 'common' })}</PharmacH6>
                <PharmacContainer
                  marginMediumTop
                  marginLargeBottom
                >
                  <PharmacText>{a.certificateCardNumber || '-'}</PharmacText>
                </PharmacContainer>
                <PharmacH6>{t('common:professionalRoles')}</PharmacH6>
                <PharmacContainer
                  marginMediumTop
                  marginLargeBottom
                >
                  {a?.professionalRoles.length > 0 ? (
                    <>
                      {a?.professionalRoles.map((a: ExtranetCustomerUserProfessionalRoleDto) => (
                        <PharmacText
                          marginSmallBottom
                          key={a.id}
                        >
                          {key(a.displayName)}
                        </PharmacText>
                      ))}
                    </>
                  ) : (
                    <>-</>
                  )}
                </PharmacContainer>
                <PharmacH6>{t('user.userRoles', { ns: 'extranet' })}</PharmacH6>
                <PharmacContainer
                  marginMediumTop
                  marginLargeBottom
                >
                  {a?.roles.map((a: ExtranetCustomerUserRoleDto) => (
                    <PharmacText
                      marginSmallBottom
                      key={a.id}
                    >
                      {key(a.displayName)}
                    </PharmacText>
                  ))}
                </PharmacContainer>
              </PharmacColumn>
            </PharmacContainer>
          </PharmacAccordionContent>
        </PharmacAccordion>
      ))}
    </>
  )
}
